"use client";

import React, { useMemo } from "react";
import { Typography, Box, Stack, useMediaQuery, useTheme, SxProps, Theme } from "@mui/material";
import { FlexibleCard as FlexibleCardType } from "@/types/dto/flexible-card-layout-section";
import Image from "next/image";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { resolveLandingPageAnchorTextColor } from "@/utils/landing-page-background";

interface FlexibleCardProps {
  card: FlexibleCardType;
  reverse?: boolean;
  index?: number;
  background?: string;
  headerSx?: SxProps<Theme>;
  copySx?: SxProps<Theme>;
}

const FlexibleCard: React.FC<FlexibleCardProps> = ({
  card,
  reverse = false,
  index = 0,
  background = "background.default",
  headerSx,
  copySx
}) => {
  const { basicInfo, image, tour } = card;
  const theme = useTheme();
  const isMobile = useMediaQuery((theme as any).breakpoints.down("mobile"));
  const anchorTextColor = resolveLandingPageAnchorTextColor(background);

  // Memoize CTA calculations
  const { ctaText, ctaUrl } = useMemo(() => {
    if (basicInfo.callToActionText && basicInfo.callToActionUrl) {
      return {
        ctaText: basicInfo.callToActionText,
        ctaUrl: basicInfo.callToActionUrl
      };
    }
    if (tour) {
      const cleanTitle = tour.title.replace(/featuring.*$/i, "").trim();
      return {
        ctaText: `View ${cleanTitle} Tour`,
        ctaUrl: tour.tourUrl
      };
    }
    return { ctaText: "", ctaUrl: "" };
  }, [basicInfo.callToActionText, basicInfo.callToActionUrl, tour]);

  const hasCallToAction = ctaText && ctaUrl;

  // Determine display text for mobile
  const displayCtaText = useMemo(() => {
    if (isMobile && ctaText.length > 25) {
      return "View Tour";
    }
    return ctaText;
  }, [isMobile, ctaText]);

  return (
    <Stack
      component="article"
      data-tour-name={tour?.title}
      data-tour-price={typeof tour?.price === "string" ? tour?.price.replace(/[^0-9]/g, "") : ""}
      data-tour-duration={tour ? String(tour.days) : undefined}
      // data-tour-activity-level={tour ? "pending-api" : undefined}
      // data-tour-style={tour ? "pending-api" : undefined}
      data-tour-id={(tour as any)?.neoId}
      data-carousel-position={typeof index === "number" ? String(index + 1) : undefined}
      gap={{ xs: "2.4rem", desktop: 0 }}
      sx={{
        display: "flex",
        flexDirection: {
          desktop: reverse ? "row-reverse" : "row",
          legacyTablet: "column"
        },
        backgroundColor: background,
        width: "100%"
      }}
    >
      <Box
        flexShrink={0}
        sx={{
          width: { desktop: "60.1rem", legacyTablet: "100%", legacyMobile: "100%" },
          height: { desktop: "47.7rem", legacyTablet: "30rem", legacyMobile: "22.5rem" },
          position: "relative"
        }}
      >
        {image.imageUrl && (
          <Image
            src={image.imageUrl}
            alt={image.altText || basicInfo.headerText || ""}
            fill
            quality={100}
            loading={index < 2 ? "eager" : "lazy"}
            priority={index === 0}
            style={{
              objectFit: "cover",
              objectPosition: "left",
              borderRadius: "2rem",
              userSelect: "none"
            }}
            sizes="(max-width: 991px) 100vw, 1720px"
            aria-hidden={!image.altText && !basicInfo.headerText ? "true" : undefined}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          padding: {
            xs: 0,
            desktop: reverse ? "6rem 6rem 0rem 2rem" : "6rem 2rem 0rem 6rem"
          },
          flexDirection: "column",
          alignItems: "flex-start",
          gap: { xs: "1.2rem", sm: "2.4rem" },
          flex: "1 0 0"
        }}
      >
        {basicInfo.headerText && (
          <Typography
            variant="h3"
            data-testid={`flexible-card-header-${index}`}
            sx={{ color: "text.primary", ...headerSx }}
          >
            {basicInfo.headerText}
          </Typography>
        )}

        {basicInfo.copyText && (
          <Box
            data-testid={`flexible-card-copy-${index}`}
            sx={{
              color: "text.primary",
              fontSize: "1.8rem",
              lineHeight: "3rem",
              width: "100%",
              fontWeight: 500,
              "& p": {
                margin: 0
              },
              "& a": { color: anchorTextColor, textDecoration: "underline !important" },
              ...copySx
            }}
            dangerouslySetInnerHTML={{ __html: basicInfo.copyText }}
          />
        )}

        {hasCallToAction && (
          <Box
            sx={{
              display: "flex",
              alignItems: { desktop: "flex-start", legacyTablet: "center", legacyMobile: "center" },
              justifyContent: {
                desktop: "flex-start",
                legacyTablet: "center",
                legacyMobile: "center"
              }
            }}
          >
            <ColletteButton
              label={displayCtaText}
              component="a"
              href={ctaUrl}
              testId={`flexible-card-cta-button-${index}`}
              data-track="click"
              sx={{
                textTransform: "none",
                fontSize: "1.4rem!important",
                padding: "1rem 2.4rem!important",
                gap: "8px!important",
                lineHeight: "2rem",
                letterSpacing: "0.01rem",
                borderRadius: "10rem!important",
                height: "40px"
              }}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default React.memo(FlexibleCard);
